import { ArrowForward } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { CampaignAuditEntry } from 'hooks/queries/useCampaignAudit';
import { CampaignStatus } from 'models/enums';
import CampaignStatusText from '../../../CampaignsTable/CampaignStatusText';
import UserAvatar from '../UserAvatar';
import { formatActivityTimestamp } from '../utils';
import { getActionConfig } from '../utils/actionConfig';

type Props = {
    index: number;
    entry: CampaignAuditEntry;
    campaignName?: string;
};
const SYSTEM_USER_EMAIL = 'system@curedhealth.com';

/**
 * Individual activity item component
 * Displays a single audit log entry with user info, action, and details
 */
const ActivityItem = ({ index, entry, campaignName }: Props) => {
    const userName = entry.created_by_email === SYSTEM_USER_EMAIL ? 'System' : entry.created_by_email;
    const timestamp = formatActivityTimestamp(entry.created_at);
    const actionConfig = getActionConfig(entry.action);
    const labelParts = actionConfig.getLabelParts(entry);
    const valueTransition = actionConfig.getValueTransition?.(entry);
    const statusTransition = actionConfig.getStatusTransition?.(entry);

    // For PUBLISHED action, display campaign name instead of scheduled timestamp
    const additionalText =
        entry.action === 'DRAFT_CREATED' && campaignName ? campaignName : actionConfig.getAdditionalText?.(entry);

    // For status transitions (STATUS_UPDATE, DRAFT_TO_PUBLISHED, PUBLISHED)
    const showStatusChips =
        actionConfig.showsStatusTransition &&
        statusTransition &&
        (statusTransition.oldValue || statusTransition.newValue);

    let showBorder = entry.action === 'DRAFT_TO_PUBLISHED' || entry.action === 'PUBLISHED';
    if (index === 0) showBorder = false;

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 2,
                py: 2,
                ...(showBorder && {
                    borderTop: 1,
                    borderColor: 'divider',
                }),
            }}
        >
            {/* Avatar */}
            <UserAvatar email={entry.created_by_email} />

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Header row: User name + action + timestamp */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                        <Typography component="span" variant="body2" fontWeight="medium">
                            {userName}
                        </Typography>{' '}
                        {labelParts.prefix}{' '}
                        <Typography component="span" variant="body2" fontWeight="bold">
                            {labelParts.bold}
                        </Typography>
                        {labelParts.suffix && ` ${labelParts.suffix}`}
                    </Typography>
                    <Typography color="text.secondary" fontSize={14} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {timestamp}
                    </Typography>
                </Stack>

                {/* Status transition chips */}
                {showStatusChips && statusTransition && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                        {statusTransition.oldValue && (
                            <CampaignStatusText status={statusTransition.oldValue.toUpperCase() as CampaignStatus} />
                        )}
                        {statusTransition.oldValue && statusTransition.newValue && (
                            <ArrowForward sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                        )}
                        {statusTransition.newValue && (
                            <CampaignStatusText status={statusTransition.newValue.toUpperCase() as CampaignStatus} />
                        )}
                    </Stack>
                )}

                {/* Value transition (for audience, content, etc. changes) */}
                {valueTransition && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {valueTransition.from} → {valueTransition.to}
                    </Typography>
                )}

                {/* Additional context text */}
                {additionalText && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {additionalText}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default ActivityItem;
